// FSD entities/media-provider — 跨媒体(海报/视频)provider 能力契约(ADR-0013)。
// 抽自原 features/poster/api/posterTemplateService(provider-capability 段)+ hooks/useProvider*,
// 去 poster 耦合:基础类型自派生 generated,poster/video feature 经 @/entities/media-provider 消费。
import { api } from "@/shared/api";
import type { components } from "@/lib/api/generated";

// 0926 第八批 Z3:generated.ts 改由 api 真 /v3/api-docs 生成后,后端这里是 String / Map,没有这个枚举/形状;
// 前后端约定的字面量留在前端这一处维护(旧 generated 里的同名定义来自手写 yaml)。
export type Provider = "WANXIANG" | "DOUBAO" | "OPENAI" | "GOOGLE" | "MOCK";
export type Ratio = "9:16" | "1:1" | "4:5" | "16:9" | "3:4";
export type Tier = "1K" | "2K" | "3K" | "4K";

/** i2i (image_edit) 子能力嵌套。 */
export interface I2iCapability {
  maxResolutionTier: Tier;
  maxCount: number;
}

/** OPENAI 独有 reasoning 路径(Responses API)子能力;其他 provider 不出现 thinking 字段。 */
export interface ThinkingCapability {
  enabled: boolean;
  supportedRatios: Ratio[];
  supportedResolutionTiers: Tier[];
  maxCount: number;
}

/** 非法 ratio×tier 组合(OPENAI 4K 总像素 ≤8.29M)。 */
export interface RatioTierCombo {
  ratio: Ratio;
  tier: Tier;
}

/** provider capability single entry(来自 ai-service capabilities.py)。 */
export interface ProviderCapability {
  enabled: boolean;
  supportedRatios: Ratio[];
  supportedResolutionTiers: Tier[];
  /** 真实 (tier, ratio) → [W, H] map。key 形如 "1K|1:1",value=[W,H]。chip tooltip 显真实像素。 */
  tierRatioSize?: Record<string, [number, number]>;
  defaultRatio: Ratio;
  defaultResolutionTier: Tier;
  /** 初始 fan-out 单次最大张数。 */
  maxCount: number;
  supportsI2i?: boolean;
  /** W2-B9:image_edit 能否一次吃多张参考图(缺省 = 不支持,提交会被拦)。 */
  supportsMultiRef?: boolean;
  /** i2i 子能力嵌套。 */
  i2i?: I2iCapability;
  /** OPENAI 独有 reasoning 子能力。 */
  thinking?: ThinkingCapability;
  /** 非笛卡尔积约束(OPENAI 4K 总像素上限)。 */
  combinationDeny?: RatioTierCombo[];
  label: string;
  /** provider 接受的 size 参数形态(调试用,前端不消费)。 */
  sizeParamShape?: "tier_or_wh" | "wh_only" | "gemini_via_openrouter_hint";
  /** provider-specific 备注;GOOGLE 注 Lanczos 上采样。 */
  note?: string;
  /** 方案 A(2026-06-15)— 每分辨率档每张积分 {tier: credits},= 后端实扣。前端按它估"预计扣 X 积分"。 */
  tierCredits?: Partial<Record<Tier, number>>;
}

export interface ProviderCapabilitiesResponse {
  providers: Record<Provider, ProviderCapability>;
}

/** 媒体资产上传响应(logo/二维码,/poster/uploads)。 */
export type UploadResponse = components["schemas"]["UploadResponse"];

/** 上传图 import 为一等 asset 的返回(/poster/import-image)。 */
export interface ImportedImage {
  taskId: number;
  assetId: number;
  baseUrl: string;
  thumbUrl: string;
  width: number;
  height: number;
}

export const mediaProviderApi = {
  getProviderCapabilities: () =>
    api.get<ProviderCapabilitiesResponse>("/poster/provider-capabilities"),

  /** 上传品牌资产(logo/二维码),跨媒体共享上传能力(原 posterService.uploadAsset)。 */
  uploadAsset: async (file: File, kind: "logo" | "qrcode"): Promise<UploadResponse> => {
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    return api.post<UploadResponse>("/poster/uploads", fd);
  },

  /** 上传图 import 为一等 media_asset(军师照片进对话),返 taskId/assetId 供落焦 + 附件盘。 */
  importImage: async (file: File): Promise<ImportedImage> => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post<ImportedImage>("/poster/import-image", fd);
  },
};
